import { DeviceCapabilitiesInterface } from '@cbsinteractive/avia-js';
import { IAutoplayInfo } from 'library/ts/interfaces/IAutoplayInfo';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { IPlayerAdapterDeviceCapabilities } from 'library/ts/player/adapter/interfaces/capabilities/IPlayerAdapterDeviceCapabilities';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { AviaExtensionsManager } from 'tag/ts/tag/player/adapters/avia/extensions/AviaExtensionsManager';
import { IAviaCapabilitiesManager } from 'tag/ts/tag/player/adapters/avia/managers/interfaces/IAviaCapabilitiesManager';
import { IAviaCapabilitiesManagerArgument } from 'tag/ts/tag/player/adapters/avia/managers/interfaces/IAviaCapabilitiesManagerArgument';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Manages device capability detection and autoplay testing for Avia player
 *
 * **Responsibilities:**
 * - **Device Capability Detection**: Detects Smart TV, STB, and Web platform capabilities
 * - **Autoplay Detection (fallback)**: Calls `window.avia.getAutoplayCapabilities()` directly
 *   when the primary library-level `checkAutoplay()` detection did not populate
 *   `window.SmartTag.autoplay`. The avia-js `AutoplayCapabilitiesService` singleton
 *   manages its own temporary `<video>` element internally — no temp player needed here.
 * - **Capability Caching**: Stores results in `window.SmartTag.deviceCapabilities`
 *
 * **Lifecycle:**
 * - Initialized once during AviaAdapter initialization
 * - Device capabilities detected on first call, cached for subsequent calls
 * - Autoplay capabilities tested on-demand via avia singleton
 *
 * **Architecture:**
 * Extracted from AviaAdapter to follow Single Responsibility Principle.
 * Reduces AviaAdapter complexity by ~120 lines.
 */
export class AviaCapabilitiesManager extends AbstractTagClass implements IAviaCapabilitiesManager {

	// Error message constants
	static readonly #DEVICE_CAPABILITIES_UNAVAILABLE = 'window?.avia?.getDeviceCapabilities() is unavailable';
	static readonly #AVIA_API_UNAVAILABLE = 'avia api not available';

	// Log message constants
	static readonly #LOG_EXECUTE_DEVICE_CAPABILITIES = 'executeDeviceCapabilitiesCheck:';
	static readonly #LOG_GET_DEVICE_CAPABILITIES = 'getDeviceCapabilities:';
	static readonly #LOG_GET_AUTOPLAY_CAPABILITIES = 'getAutoplayCapabilities:';

	constructor( argument: IAviaCapabilitiesManagerArgument ) {
		super(
			{
				...argument,
				name: 'AviaCapabilitiesManager'
			}
		);
	}

	/**
	 * @description Detects and caches device capabilities using avia.getDeviceCapabilities()
	 * Only runs once per session - subsequent calls return cached result
	 * @returns {Promise<IPromiseResolveGeneric & {capabilities?: DeviceCapabilitiesInterface}>}
	 */
	detectDeviceCapabilities = (): Promise<IPromiseResolveGeneric & { capabilities?: DeviceCapabilitiesInterface }> => {
		return new Promise(
			( resolve, reject ) => {
				/**
				 * @description Checks if device capabilities were detected and starts detection if not
				 */
				const checkDeviceCapabilitiesNamespace = (): void => {
					// check if device capabilities were already detected + stored
					if ( !this.utils.Type.isEmpty( window?.SmartTag?.deviceCapabilities ) ) {
						resolve( { msg: 'Device capabilities result already exists.', result: window?.SmartTag?.deviceCapabilities } );
					} else {
						// otherwise => run the check
						this.#executeDeviceCapabilitiesCheck( resolve, reject );
					}
				};

				checkDeviceCapabilitiesNamespace();
			}
		);
	};

	/**
	 * @description Executes HTML5 device detection logic
	 * @param {(value: IPromiseResolveGeneric & { capabilities?: IPlayerAdapterDeviceCapabilities }) => void} resolve - Promise resolve function
	 * @param {(reason?: unknown) => void} reject - Promise reject function
	 * @private
	 */
	#executeDeviceCapabilitiesCheck = ( resolve: ( value: IPromiseResolveGeneric & {
		capabilities?: IPlayerAdapterDeviceCapabilities
	} ) => void, reject: ( reason?: unknown ) => void ): void => {
		if ( this.utils.Type.isNullOrUndefined( window?.avia?.getDeviceCapabilities ) ) {
			this?.logger?.warn( AviaCapabilitiesManager.#LOG_EXECUTE_DEVICE_CAPABILITIES, AviaCapabilitiesManager.#DEVICE_CAPABILITIES_UNAVAILABLE );
			return reject( AviaCapabilitiesManager.#DEVICE_CAPABILITIES_UNAVAILABLE );
		}

		const extensionsManager = new AviaExtensionsManager( { tag: this?.tag } );

		extensionsManager.loadCapabilitiesExtensions()
			.then( window?.avia?.getDeviceCapabilities )
			.then( ( capabilities ) => {
				this?.logger?.log( AviaCapabilitiesManager.#LOG_EXECUTE_DEVICE_CAPABILITIES, 'getDeviceCapabilities promise resolved', capabilities );

					// store device capabilities for exposure
				window.SmartTag.deviceCapabilities = this.utils.DeepMerge.deepmerge( {}, capabilities ) as IPlayerAdapterDeviceCapabilities;

					// resolve promise with delay
				this.utils.Delay.delay().then( () => resolve( { msg: 'Device capabilities detected.', result: window.SmartTag.deviceCapabilities } ) );
			},
			( reason ) => {
				this?.logger?.warn( AviaCapabilitiesManager.#LOG_EXECUTE_DEVICE_CAPABILITIES, 'getDeviceCapabilities promise rejected', reason );
				reject( reason );
			} );
	};

	/**
	 * @description Gets autoplay capabilities via avia-js singleton.
	 * Calls `window.avia.getAutoplayCapabilities()` directly — the avia-js
	 * `AutoplayCapabilitiesService` singleton creates its own temporary `<video>`
	 * element internally and caches the result. No temporary player instance,
	 * DOM container, or cleanup is needed on our side.
	 *
	 * This is a **fallback path** — the primary detection runs earlier via
	 * `SmartTagSystemOperations` → library `checkAutoplay()`. This method is
	 * only called when `window.SmartTag.autoplay` is still empty.
	 * @return {Promise<{autoplayInfo: IAutoplayInfo}>}
	 */
	getAutoplayCapabilities = async (): Promise<{ autoplayInfo: IAutoplayInfo }> => {
		try {
			if ( this.utils.Type.isNullOrUndefined( window?.avia?.getAutoplayCapabilities ) ) {
				this?.logger?.warn( AviaCapabilitiesManager.#LOG_GET_AUTOPLAY_CAPABILITIES, 'window.avia.getAutoplayCapabilities is unavailable' );
				return { autoplayInfo: null };
			}

			const autoplayInfo: IAutoplayInfo = await window.avia.getAutoplayCapabilities();
			this?.logger?.log( AviaCapabilitiesManager.#LOG_GET_AUTOPLAY_CAPABILITIES, 'avia singleton returned', autoplayInfo );
			return { autoplayInfo };

		} catch ( error ) {
			this?.logger?.warn( AviaCapabilitiesManager.#LOG_GET_AUTOPLAY_CAPABILITIES, 'Autoplay capabilities detection failed:', error );
			return { autoplayInfo: null };
		}
	};

	/**
	 * @description Get device capabilities (synchronous from cache)
	 * @returns {Promise<IPlayerAdapterDeviceCapabilities>}
	 */
	getDeviceCapabilities = (): Promise<IPlayerAdapterDeviceCapabilities> => {
		this?.logger?.log( 'getDeviceCapabilities' );

		return new Promise(
			( resolve, reject ) => {
				if ( this.utils.Type.isNullOrUndefined( window?.avia?.getDeviceCapabilities ) ) {
					this?.logger?.warn( AviaCapabilitiesManager.#LOG_GET_DEVICE_CAPABILITIES, AviaCapabilitiesManager.#AVIA_API_UNAVAILABLE );
					reject( AviaCapabilitiesManager.#AVIA_API_UNAVAILABLE );
					return;
				}

				window.avia?.getDeviceCapabilities()?.then( ( result ) => {
					this?.logger?.log( AviaCapabilitiesManager.#LOG_GET_DEVICE_CAPABILITIES, 'avia promise responded with', result );
					// 🔄 ARCHITECTURAL BOUNDARY: avia-js → Player Adapter
					// Converts internal DeviceCapabilitiesInterface to external IPlayerAdapterDeviceCapabilities
					// Safe: identical underlying structure, just different type definitions
					resolve( result as IPlayerAdapterDeviceCapabilities );
				}, ( reason ) => {
					this?.logger?.warn( AviaCapabilitiesManager.#LOG_GET_DEVICE_CAPABILITIES, 'avia promise rejected because', reason );
					reject( reason );
				} );
			}
		);
	};

}